import { Paper, Skeleton, styled, Typography } from '@mui/material';
import clsx from 'clsx';
import { isDefined } from 'lib/typeguards';
import React from 'react';
import { Row } from 'ui/Flex';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import {
    AudienceMeasureConfig,
    AudienceMeasureTotal,
    EAudienceMeasureType,
    formatAudienceAnalyticsMeasureValue
} from './model';
import { useCurrencyString } from 'lib/hooks/useCurrencyString';
import { TotalsBoxPopup } from './TotalsBoxPopup';

interface TotalsBoxProps {
    active?: boolean;
    measure: AudienceMeasureTotal;
    onClick?: (key: string) => void;
    datePoints: [Date, Date, Date];
    config: Record<EAudienceMeasureType, AudienceMeasureConfig>;
}

const classes = {
    active: 'TotalsBox-active',
    clickable: 'TotalsBox-clickable'
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    minHeight: '112px',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    [`&.${classes.clickable}`]: {
        cursor: 'pointer',
        background: theme.palette.grey[100],
        '&:hover': {
            boxShadow: theme.shadows[3]
        }
    },
    [`&.${classes.active}`]: {
        cursor: 'default',
        background: theme.palette.background.paper,
        boxShadow: theme.shadows[1]
    }
}));

const InteractiveTypography = styled(Typography)(() => ({
    textDecoration: 'underline',
    textDecorationStyle: 'dashed',
    cursor: 'help',
    '&:hover': {
        textDecorationStyle: 'solid'
    }
}));

const InteractiveCaption = styled(Typography)(({ theme }) => ({
    cursor: 'help',
    position: 'relative',
    '&:before': {
        position: 'absolute',
        content: 'no-open-quote',
        display: 'block',
        bottom: theme.spacing(0.25),
        left: theme.spacing(0.5),
        right: theme.spacing(0.5),
        borderColor: 'inherit',
        borderBottom: '1px dashed'
    },
    '&:hover': {
        '&:before': {
            borderBottom: '1px solid'
        }
    }
}));

export const TotalsBox: React.FC<TotalsBoxProps> = ({ active, measure, onClick, datePoints, config }) => {
    const getCurrencyString = useCurrencyString();
    const { title, subtitle, format, description } = React.useMemo(
        () => config[measure.key],
        [config, measure.key]
    );
    const value = React.useMemo(
        () => formatAudienceAnalyticsMeasureValue(measure.current?.value, format, getCurrencyString),
        [format, getCurrencyString, measure]
    );
    const retroValue = React.useMemo(
        () => formatAudienceAnalyticsMeasureValue(measure.retro?.value, format, getCurrencyString),
        [format, getCurrencyString, measure]
    );
    const difference = React.useMemo(
        () => (measure.current?.value ?? 0) - (measure.retro?.value ?? 0),
        [measure]
    );

    const currentValuesLoading = React.useMemo(() => measure.current?.loading ?? true, [measure]);
    const retroValuesLoading = React.useMemo(() => measure.retro?.loading ?? true, [measure.retro?.loading]);

    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(measure.key);
        }
    }, [measure.key, onClick]);

    const [anchor, setAnchor] = React.useState<HTMLDivElement | null>(null);
    const handleMouseEnter = React.useCallback((e: React.MouseEvent) => {
        setAnchor(e.currentTarget as HTMLDivElement);
    }, []);
    const handleMouseLeave = React.useCallback(() => {
        setAnchor(null);
    }, []);

    return (
        <StyledPaper
            className={clsx({ [classes.clickable]: !!onClick, [classes.active]: active })}
            elevation={0}
            onClick={handleClick}
        >
            <InteractiveTypography
                variant="body2"
                sx={{
                    fontWeight: 'bold',
                    opacity: 0.75
                }}
                aria-owns={!!anchor ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {title}
            </InteractiveTypography>
            <Row valign="flex-end">
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 'bold',
                        mt: 0.25
                    }}
                >
                    {currentValuesLoading ? <Skeleton width={100} /> : value}
                </Typography>
                {retroValuesLoading && <Skeleton sx={{ ml: 0.5 }} variant="text" width={60} height={26} />}
                {!retroValuesLoading && isDefined(measure.difference) && (
                    <InteractiveCaption
                        variant="caption"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        sx={{
                            color: measure.difference >= 0 ? 'success.main' : 'error.main',
                            borderColor: measure.difference >= 0 ? 'success.main' : 'error.main',
                            ml: 0.5,
                            pb: 0.25,
                            fontWeight: 600,
                            fontSize: '0.8rem'
                        }}
                    >
                        (
                        {new Intl.NumberFormat('en-GB', {
                            style: 'percent',
                            maximumFractionDigits: 1
                        }).format(measure.difference)}
                        <ArrowOutwardIcon
                            color="inherit"
                            sx={{
                                fontSize: '0.8rem',
                                transform: measure.difference < 0 ? 'rotate(90deg)' : undefined,
                                mt: 0.25
                            }}
                        />
                        )
                    </InteractiveCaption>
                )}
            </Row>
            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6, mt: 0.25 }}>
                {subtitle}
            </Typography>
            <TotalsBoxPopup
                title={title}
                subtitle={description}
                anchor={anchor}
                onClose={handleMouseLeave}
                datePoints={datePoints}
                value={value}
                retroValue={retroValue}
                valueLoading={currentValuesLoading}
                retroValueLoading={retroValuesLoading}
                difference={difference}
            />
        </StyledPaper>
    );
};
